import React, { useState, useEffect } from 'react'
import {
  Modal,
  Spin,
  Empty,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Card,
  Icon,
  Tooltip,
  Button,
  Collapse,
  Divider,
  Table
} from 'antd'
import { getVideoAnalysis } from '../../../../services/api/aiVideoGeneration'
import moment from 'moment'
import './VideoAnalytics.scss'

const { Panel } = Collapse

const VideoAnalytics = ({ visible, onClose, videoId, userId, videoTitle }) => {
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [analytics, setAnalytics] = useState(null)

  useEffect(() => {
    if (visible && videoId && userId) {
      setAnalytics(null)
      setErrorMsg('')
      fetchAnalytics()
    }
  }, [visible, videoId, userId])

  const fetchAnalytics = async () => {
    setLoading(true)
    setErrorMsg('')
    try {
      const response = await getVideoAnalysis(userId, videoId)
      setAnalytics(response.data || response)
    } catch (error) {
      console.error('Failed to fetch video analytics:', error)
      const detail =
        error.response?.data?.detail ||
        error.message ||
        'Failed to load video analytics'
      setErrorMsg(detail)
      message.error(detail)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 3
    }).format(value || 0)
  }

  const formatSeconds = seconds => {
    if (!seconds) return '0s'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.round(seconds % 60)
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  const renderDashboard = () => {
    if (!analytics) return null

    const {
      video_id,
      token_summary = {},
      total_api_calls = 0,
      total_images = 0,
      total_videos = 0,
      total_audio = 0,
      total_video_duration_seconds = 0,
      total_audio_duration_seconds = 0,
      total_audio_characters = 0,
      total_processing_seconds = 0,
      estimated_costs = {},
      first_activity,
      last_activity,
      token_usage = {},
      api_calls = {}
    } = analytics

    // Hardcoded lists to ensure everything shows up even if 0
    const PROVIDERS = ['gemini', 'openai', 'anthropic', 'grok']
    const SERVICES = [
      'gemini',
      'openai',
      'anthropic',
      'grok',
      'elevenlabs',
      'nano-banana'
    ]

    return (
      <div className="video-analytics-dashboard">
        <div className="dashboard-header">
          <span className="dashboard-header__title">AI Video Report</span>
          <span className="dashboard-header__id">
            Video ID: {video_id || videoId}
          </span>
        </div>

        {/* Top Statistics */}
        <Row gutter={[16, 16]} className="stat-grid">
          <Col span={6}>
            <Card>
              <Statistic
                title="Estimated Cost"
                value={estimated_costs.total || 0}
                precision={3}
                prefix={<Icon type="dollar" />}
                suffix="USD"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total API Calls"
                value={total_api_calls || 0}
                prefix={<Icon type="api" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Tokens Processed"
                value={token_summary.total || 0}
                prefix={<Icon type="field-number" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Assets"
                value={
                  (total_images || 0) + (total_videos || 0) + (total_audio || 0)
                }
                prefix={<Icon type="picture" />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={24} style={{ marginTop: '24px' }}>
          {/* Left Column: Usage Details */}
          <Col span={14}>
            <div className="section-title">
              <Icon type="rocket" /> AI Usage Details
            </div>
            <div className="usage-card">
              {/* Token Summary */}
              <div className="usage-row" style={{ background: '#f8fafc' }}>
                <div className="usage-row__label">
                  <Icon type="database" style={{ color: '#0ea5e9' }} /> Token
                  Summary
                </div>
                <div className="usage-row__value" style={{ fontWeight: '600' }}>
                  {(token_summary.total || 0).toLocaleString()} total
                </div>
              </div>
              <div className="usage-row" style={{ paddingLeft: '24px' }}>
                <div className="usage-row__label">
                  <Icon type="arrow-down" style={{ color: '#22c55e' }} /> Total
                  Input
                </div>
                <div className="usage-row__value">
                  {(token_summary.total_input || 0).toLocaleString()}
                </div>
              </div>
              <div
                className="usage-row"
                style={{ paddingLeft: '24px', paddingBottom: '12px' }}
              >
                <div className="usage-row__label">
                  <Icon type="arrow-up" style={{ color: '#f59e0b' }} /> Total
                  Output
                </div>
                <div className="usage-row__value">
                  {(token_summary.total_output || 0).toLocaleString()}
                </div>
              </div>

              {/* Token Usage by Provider */}
              <Divider style={{ margin: '12px 0' }} />
              {PROVIDERS.map(provider => {
                const data = token_usage[provider] || {
                  input: 0,
                  output: 0,
                  total: 0
                }
                return (
                  <div className="usage-row" key={provider}>
                    <div className="usage-row__label">
                      <Tag
                        className="provider-tag"
                        color={
                          provider === 'gemini'
                            ? 'blue'
                            : provider === 'openai'
                            ? 'green'
                            : provider === 'anthropic'
                            ? 'orange'
                            : 'purple'
                        }
                      >
                        {provider.toUpperCase()}
                      </Tag>
                      In: {(data.input || 0).toLocaleString()} / Out:{' '}
                      {(data.output || 0).toLocaleString()}
                    </div>
                    <div className="usage-row__value">
                      Total: {(data.total || 0).toLocaleString()}
                    </div>
                  </div>
                )
              })}

              {/* API Calls Breakdown */}
              <Divider style={{ margin: '12px 0' }} />
              <div className="usage-row" style={{ background: '#f8fafc' }}>
                <div className="usage-row__label">
                  <Icon type="cloud-server" /> API Calls by Service
                </div>
                <div className="usage-row__value">
                  {total_api_calls || 0} total
                </div>
              </div>
              {SERVICES.map(service => {
                const count = api_calls[service] || 0
                return (
                  <div
                    className="usage-row"
                    key={service}
                    style={{ paddingLeft: '24px' }}
                  >
                    <div className="usage-row__label">
                      <Icon type="dot-chart" /> {service}
                    </div>
                    <div className="usage-row__value">{count} calls</div>
                  </div>
                )
              })}

              {/* Asset Generation */}
              <Divider style={{ margin: '12px 0' }} />
              <div className="usage-row" style={{ background: '#f8fafc' }}>
                <div className="usage-row__label">
                  <Icon type="picture" /> Asset Generation
                </div>
                <div className="usage-row__value">
                  {(total_images || 0) +
                    (total_videos || 0) +
                    (total_audio || 0)}{' '}
                  total
                </div>
              </div>
              <div className="usage-row" style={{ paddingLeft: '24px' }}>
                <div className="usage-row__label">
                  <Icon type="file-image" /> Images
                </div>
                <div className="usage-row__value">{total_images || 0}</div>
              </div>
              <div className="usage-row" style={{ paddingLeft: '24px' }}>
                <div className="usage-row__label">
                  <Icon type="video-camera" /> Videos
                </div>
                <div className="usage-row__value">{total_videos || 0}</div>
              </div>
              <div className="usage-row" style={{ paddingLeft: '24px' }}>
                <div className="usage-row__label">
                  <Icon type="audio" /> Audio Files
                </div>
                <div className="usage-row__value">{total_audio || 0}</div>
              </div>

              {/* Media Duration */}
              <Divider style={{ margin: '12px 0' }} />
              <div className="usage-row">
                <div className="usage-row__label">
                  <Icon type="play-square" /> Video Duration
                </div>
                <div className="usage-row__value">
                  {total_video_duration_seconds || 0}s
                </div>
              </div>
              <div className="usage-row">
                <div className="usage-row__label">
                  <Icon type="sound" /> Audio Duration
                </div>
                <div className="usage-row__value">
                  {total_audio_duration_seconds || 0}s
                </div>
              </div>
              <div className="usage-row">
                <div className="usage-row__label">
                  <Icon type="file-text" /> Audio Characters
                </div>
                <div className="usage-row__value">
                  {(total_audio_characters || 0).toLocaleString()}
                </div>
              </div>

              {/* Processing Time */}
              <Divider style={{ margin: '12px 0' }} />
              <div className="usage-row" style={{ background: '#f8fafc' }}>
                <div className="usage-row__label">
                  <Icon type="clock-circle" style={{ color: '#8b5cf6' }} />{' '}
                  Processing Time
                </div>
                <div className="usage-row__value" style={{ fontWeight: '600' }}>
                  {formatSeconds(total_processing_seconds)}
                </div>
              </div>
              {estimated_costs.processing_breakdown && (
                <>
                  <div className="usage-row" style={{ paddingLeft: '24px' }}>
                    <div className="usage-row__label">
                      <Icon type="video-camera" /> Video Processing
                    </div>
                    <div className="usage-row__value">
                      {formatSeconds(
                        estimated_costs.processing_breakdown.video_seconds || 0
                      )}
                    </div>
                  </div>
                  <div className="usage-row" style={{ paddingLeft: '24px' }}>
                    <div className="usage-row__label">
                      <Icon type="file-image" /> Image Processing
                    </div>
                    <div className="usage-row__value">
                      {formatSeconds(
                        estimated_costs.processing_breakdown.image_seconds || 0
                      )}
                    </div>
                  </div>
                  <div className="usage-row" style={{ paddingLeft: '24px' }}>
                    <div className="usage-row__label">
                      <Icon type="audio" /> Audio Processing
                    </div>
                    <div className="usage-row__value">
                      {formatSeconds(
                        estimated_costs.processing_breakdown.audio_seconds || 0
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="section-title" style={{ marginTop: '24px' }}>
              <Icon type="history" /> Activity Timeline
            </div>
            <div className="activity-timeline">
              <div className="timeline-item">
                <div className="timeline-item__dot timeline-item__dot--start" />
                <div className="timeline-item__label">Generation Start</div>
                <div className="timeline-item__time">
                  {first_activity
                    ? moment(first_activity).format('MMM D, YYYY HH:mm:ss')
                    : 'N/A'}
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-item__dot timeline-item__dot--end" />
                <div className="timeline-item__label">Final Activity</div>
                <div className="timeline-item__time">
                  {last_activity
                    ? moment(last_activity).format('MMM D, YYYY HH:mm:ss')
                    : 'N/A'}
                </div>
              </div>
              {first_activity && last_activity && (
                <div className="timeline-item">
                  <div className="timeline-item__dot" />
                  <div className="timeline-item__label">Total Duration</div>
                  <div className="timeline-item__time">
                    {moment
                      .duration(moment(last_activity).diff(first_activity))
                      .humanize()}
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Right Column: Cost Attribution */}
          <Col span={10}>
            <div className="section-title">
              <Icon type="bank" /> Cost Attribution
            </div>
            <div className="cost-breakdown">
              {/* LLM Costs */}
              <div className="cost-category">
                <div className="cost-category__header">
                  <Icon type="bulb" /> Language Models
                </div>
                <div className="cost-item">
                  <span className="cost-item__label">Gemini (Input)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.gemini_input)}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-item__label">Gemini (Output)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.gemini_output)}
                  </span>
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Gemini Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.gemini)}
                  </span>
                </div>

                <div className="cost-item">
                  <span className="cost-item__label">OpenAI (Input)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.openai_input)}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-item__label">OpenAI (Output)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.openai_output)}
                  </span>
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">OpenAI Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.openai)}
                  </span>
                </div>

                <div className="cost-item">
                  <span className="cost-item__label">Anthropic (Input)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.anthropic_input)}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-item__label">Anthropic (Output)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.anthropic_output)}
                  </span>
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Anthropic Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.anthropic)}
                  </span>
                </div>

                <div className="cost-item">
                  <span className="cost-item__label">Grok (Input)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.grok_input)}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-item__label">Grok (Output)</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.grok_output)}
                  </span>
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Grok Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.grok)}
                  </span>
                </div>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Image Costs */}
              <div className="cost-category">
                <div className="cost-category__header">
                  <Icon type="picture" /> Visual Assets
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Images Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.images)}
                  </span>
                </div>
                {estimated_costs.images_breakdown &&
                  Object.entries(estimated_costs.images_breakdown).map(
                    ([provider, cost]) => (
                      <div className="cost-item" key={provider}>
                        <span className="cost-item__label">→ {provider}</span>
                        <span className="cost-item__value">
                          {formatCurrency(cost)}
                        </span>
                      </div>
                    )
                  )}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Video Costs */}
              <div className="cost-category">
                <div className="cost-category__header">
                  <Icon type="video-camera" /> Video Generation
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Videos Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.videos)}
                  </span>
                </div>
                {estimated_costs.videos_breakdown &&
                  Object.entries(estimated_costs.videos_breakdown).map(
                    ([provider, cost]) => (
                      <div className="cost-item" key={provider}>
                        <span className="cost-item__label">→ {provider}</span>
                        <span className="cost-item__value">
                          {formatCurrency(cost)}
                        </span>
                      </div>
                    )
                  )}
                {(estimated_costs.wan_videos > 0 ||
                  estimated_costs.seedance_videos > 0) && (
                  <>
                    <div
                      className="cost-item"
                      style={{ paddingLeft: '8px', marginTop: '4px' }}
                    >
                      <span
                        className="cost-item__label"
                        style={{ color: '#94a3b8', fontSize: '11px' }}
                      >
                        WAN Videos
                      </span>
                      <span
                        className="cost-item__value"
                        style={{ color: '#94a3b8', fontSize: '11px' }}
                      >
                        {formatCurrency(estimated_costs.wan_videos)}
                      </span>
                    </div>
                    <div className="cost-item" style={{ paddingLeft: '8px' }}>
                      <span
                        className="cost-item__label"
                        style={{ color: '#94a3b8', fontSize: '11px' }}
                      >
                        Seedance Videos
                      </span>
                      <span
                        className="cost-item__value"
                        style={{ color: '#94a3b8', fontSize: '11px' }}
                      >
                        {formatCurrency(estimated_costs.seedance_videos)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Audio Costs */}
              <div className="cost-category">
                <div className="cost-category__header">
                  <Icon type="audio" /> Audio Generation
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Audio Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.audio)}
                  </span>
                </div>
                {estimated_costs.audio_breakdown &&
                  Object.entries(estimated_costs.audio_breakdown).map(
                    ([provider, cost]) => (
                      <div className="cost-item" key={provider}>
                        <span className="cost-item__label">→ {provider}</span>
                        <span className="cost-item__value">
                          {formatCurrency(cost)}
                        </span>
                      </div>
                    )
                  )}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Infrastructure Costs */}
              <div className="cost-category">
                <div className="cost-category__header">
                  <Icon type="cloud-server" /> Infrastructure
                </div>
                <div className="cost-item cost-item--subtotal">
                  <span className="cost-item__label">Infra Total</span>
                  <span className="cost-item__value">
                    {formatCurrency(estimated_costs.infra)}
                  </span>
                </div>
                {estimated_costs.infra_breakdown &&
                  Object.entries(estimated_costs.infra_breakdown).map(
                    ([provider, cost]) => (
                      <div className="cost-item" key={provider}>
                        <span className="cost-item__label">→ {provider}</span>
                        <span className="cost-item__value">
                          {formatCurrency(cost)}
                        </span>
                      </div>
                    )
                  )}
                <div className="cost-item" style={{ marginTop: '8px' }}>
                  <span className="cost-item__label">
                    <Icon type="clock-circle" /> Total Processing
                  </span>
                  <span className="cost-item__value">
                    {formatSeconds(
                      estimated_costs.total_processing_seconds || 0
                    )}
                  </span>
                </div>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Total */}
              <div className="cost-item cost-item--total">
                <span className="cost-item__label">
                  <Icon type="dollar-circle" /> Total Production Cost
                </span>
                <span className="cost-item__value">
                  {formatCurrency(estimated_costs.total)}
                </span>
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <Tooltip title="Costs are estimated based on current provider rates and token density.">
                <div
                  style={{
                    fontSize: '11px',
                    color: '#94a3b8',
                    textAlign: 'right',
                    fontStyle: 'italic'
                  }}
                >
                  <Icon type="info-circle" /> Estimates updated{' '}
                  {moment().format('HH:mm')}
                </div>
              </Tooltip>
            </div>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <Modal
      maskClosable={false}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon type="dashboard" style={{ color: '#5057d5' }} />
          <span>Analytics Insights</span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={1100}
      bodyStyle={{ padding: '32px', backgroundColor: '#fff' }}
      className="analytics-dashboard-modal"
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" tip="Aggregating usage metrics..." />
        </div>
      ) : errorMsg ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="analytics-error-state">
              <span
                style={{
                  color: '#64748b',
                  display: 'block',
                  marginBottom: '16px'
                }}
              >
                {errorMsg}
              </span>
              <Button
                type="primary"
                icon="reload"
                onClick={fetchAnalytics}
                style={{ borderRadius: '6px', height: '38px' }}
              >
                Retry Request
              </Button>
            </div>
          }
          style={{ padding: '60px 0' }}
        />
      ) : analytics ? (
        renderDashboard()
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="We couldn't find any usage metrics for this video yet."
          style={{ padding: '60px 0' }}
        />
      )}
    </Modal>
  )
}

export default VideoAnalytics
